import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { IconButton } from "@atlaskit/button-next/new";
import { NewCoreIconProps } from "@atlaskit/icon";
import CopyIcon from "@atlaskit/icon/core/copy";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import ThumbsDownIcon from "@atlaskit/icon/core/thumbs-down";
import ThumbsUpIcon from "@atlaskit/icon/core/thumbs-up";
import Link from "@atlaskit/link";
import { token } from "@atlaskit/tokens";

import { Tracking } from "@/analytics/atl-analytics/tracking";
import { useLocalizedHref } from "@/libraries/web-framework";

import { useAISummaryContext } from "./ai-summary-context";

const aiSummaryFooterStyle = css({
    display: "flex",
    justifyContent: "space-between",
    padding: token("space.050"),
    marginBlockEnd: token("space.negative.200")
});

const aiSummaryFooterSectionStyle = css({
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: token("space.200"),
    fontSize: "12px",
    lineHeight: token("space.200")
});

const aiFeedbackSectionStyle = css({
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-end"
});

const disclaimerTextStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: token("space.050")
});

const subtleLinkOverrideStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& a": {
        color: `${token("color.text.subtlest")}`
    }
});

const FeedbackThumpsUpIcon = (
    iconProps: NewCoreIconProps,
    feedbackState: "thumbsUp" | "thumbsDown" | null
) => {
    return (
        <ThumbsUpIcon
            {...iconProps}
            color={
                feedbackState === "thumbsUp"
                    ? token("color.icon.selected")
                    : undefined
            }
        />
    );
};

const FeedbackThumpsDownIcon = (
    iconProps: NewCoreIconProps,
    feedbackState: "thumbsUp" | "thumbsDown" | null
) => {
    return (
        <ThumbsDownIcon
            {...iconProps}
            color={
                feedbackState === "thumbsDown"
                    ? token("color.icon.selected")
                    : undefined
            }
        />
    );
};

// eslint-disable-next-line complexity
export const AISummaryFooter = () => {
    const [feedbackState, setFeedbackState] = useState<
        "thumbsUp" | "thumbsDown" | null
    >(null);

    const intl = useIntl();
    const aiHref = useLocalizedHref("/trust/atlassian-intelligence");
    const policyHref = useLocalizedHref("/legal/acceptable-use-policy");
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { summary, likeFeedback, dislikeFeedback } = useAISummaryContext();

    useEffect(() => {
        Tracking.autoTrackLinks(true);
    }, []);

    const handleCopy = async () => {
        try {
            // Remove citation markers like [1], [2], etc. from the text
            const textToCopy = summary.replace(/\[\d+\]/g, "").trim();
            await navigator.clipboard.writeText(textToCopy);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    const handleFeedbackClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        state: "thumbsUp" | "thumbsDown"
    ) => {
        event.preventDefault();
        setFeedbackState(state);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "feedbackButton",
            actionSubjectId: "searchAiSummaryFeedbackClicked",
            source: "searchAiSummary",
            attributes: {
                feedback: state
            }
        }).fire();

        if (state === "thumbsUp") {
            likeFeedback();
        } else {
            dislikeFeedback();
        }
    };

    const aiDisclaimerText = intl.formatMessage({
        id: "ai.summary.answer.footer.disclaimer",
        defaultMessage: "Uses AI. Verify results."
    });

    const aiPolicyText = intl.formatMessage({
        id: "ai.summary.answer.footer.policy",
        defaultMessage: "Subject to Atlassian Acceptable Use Policy"
    });

    const thumbsUpText = intl.formatMessage({
        id: "ai.summary.answer.footer.thumbsUp",
        defaultMessage: "Good response"
    });

    const thumbsDownText = intl.formatMessage({
        id: "ai.summary.answer.footer.thumbsDown",
        defaultMessage: "Bad response"
    });

    const copyText = intl.formatMessage({
        id: "ai.summary.answer.footer.copy",
        defaultMessage: "Copy"
    });

    return (
        <div css={aiSummaryFooterStyle}>
            <div css={aiSummaryFooterSectionStyle}>
                <span css={[disclaimerTextStyle, subtleLinkOverrideStyle]}>
                    <InformationCircleIcon
                        color={token("color.icon.subtlest")}
                        size="medium"
                        label=""
                    />
                    <Link
                        href={aiHref}
                        aria-label={aiDisclaimerText}
                        target="_blank"
                        appearance="subtle"
                        data-event={{
                            "data-product-key": "search",
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "searchAiSummary",
                            "data-label-english": "Uses AI"
                        }}
                    >
                        {aiDisclaimerText}
                    </Link>
                </span>
                <span css={subtleLinkOverrideStyle}>
                    <Link
                        href={policyHref}
                        aria-label={aiPolicyText}
                        target="_blank"
                        appearance="subtle"
                        data-event={{
                            "data-product-key": "search",
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "searchAiSummary",
                            "data-label-english":
                                "Subject to Atlassian Acceptable Use Policy"
                        }}
                    >
                        {aiPolicyText}
                    </Link>
                </span>
            </div>
            <div css={aiFeedbackSectionStyle}>
                <IconButton
                    appearance="subtle"
                    icon={(iconProps) =>
                        FeedbackThumpsUpIcon(iconProps, feedbackState)
                    }
                    label={thumbsUpText}
                    onClick={(event) => handleFeedbackClick(event, "thumbsUp")}
                    isTooltipDisabled={false}
                />
                <IconButton
                    appearance="subtle"
                    icon={(iconProps) =>
                        FeedbackThumpsDownIcon(iconProps, feedbackState)
                    }
                    label={thumbsDownText}
                    onClick={(event) =>
                        handleFeedbackClick(event, "thumbsDown")
                    }
                    isTooltipDisabled={false}
                />
                <IconButton
                    appearance="subtle"
                    icon={CopyIcon}
                    label={copyText}
                    onClick={handleCopy}
                    isTooltipDisabled={false}
                />
            </div>
        </div>
    );
};
